import React, { useState } from 'react';
import { usePageMeta } from '../utils/pageMeta';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Box, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';

const gold = '#C9A84C';
const inputSx = {
  '& .MuiInputBase-input': { color: 'white' },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(201,168,76,0.35)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: gold },
};

export default function ContactUs() {
  usePageMeta('Contact Us | {club}', 'Contact {club} in Orlando, FL. Reach Club and Facility Director Steve Long by phone, email, or our contact form.');
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', website: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    setError('');
    if (!form.name.trim() || !form.message.trim()) { setError('Please enter your name and a message.'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) { setError('Please enter a valid email address.'); return; }
    setSending(true);
    try {
      const functions = getFunctions();
      const send = httpsCallable(functions, 'sendContactMessage');
      await send(form);
      setSent(true);
    } catch (e) {
      setError('Failed to send. Please try again, or call us directly.');
    }
    setSending(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#0D1E35', display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, py: 4 }}>
      <Box component="img" src="/horzlogo.png" alt="Torch Beach Volleyball"
        sx={{ maxWidth: 300, width: '75vw', cursor: 'pointer', mb: 3 }} onClick={() => navigate('/')} />

      <Box sx={{ width: '100%', maxWidth: 560 }}>
        <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '2rem', letterSpacing: 3, color: gold, textAlign: 'center' }}>
          Contact Us
        </Typography>

        <Box sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.85)', mb: 3 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Steve Long</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', mb: 0.5 }}>Club and Facility Director</Typography>
          <Typography>
            <a href="tel:+13212448708" style={{ color: gold, textDecoration: 'none', fontWeight: 600 }}>321.244.8708</a>
            {' · '}
            <a href="mailto:info@torchvolleyball.com" style={{ color: gold, textDecoration: 'none', fontWeight: 600 }}>info@torchvolleyball.com</a>
          </Typography>
        </Box>

        {/* Common questions — add more docs here as they're created */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 700, color: gold, mb: 1, fontSize: '0.95rem', letterSpacing: 1 }}>
            COMMON QUESTIONS
          </Typography>
          {[
            { label: 'Junior Club Info for Parents', href: '/parent-info.pdf' },
          ].map(({ label, href }) => (
            <Typography key={href} sx={{ mb: 0.5 }}>
              <a href={href} target="_blank" rel="noopener noreferrer"
                 style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'underline', textDecorationColor: 'rgba(201,168,76,0.5)' }}>
                📋 {label}
              </a>
            </Typography>
          ))}
        </Box>

        {sent ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Message sent! We'll get back to you soon.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
            <TextField fullWidth label="Your Name *" value={form.name} onChange={set('name')} sx={inputSx} />
            <TextField fullWidth label="Email *" type="email" value={form.email} onChange={set('email')} sx={inputSx} />
            <TextField fullWidth label="Phone (optional)" value={form.phone} onChange={set('phone')} sx={inputSx} />
            <TextField fullWidth label="Message *" value={form.message} onChange={set('message')} multiline rows={5} sx={inputSx} />
            {/* Honeypot — hidden from humans, bots fill it and get silently dropped */}
            <input type="text" name="website" value={form.website} onChange={set('website')}
              style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }}
              tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <Button variant="contained" disabled={sending} onClick={handleSubmit}
              sx={{ backgroundColor: gold, color: '#0D1E35', fontWeight: 700, py: 1.2, '&:hover': { backgroundColor: '#e0be6a' } }}>
              {sending ? <CircularProgress size={22} sx={{ color: '#0D1E35' }} /> : 'Send Message'}
            </Button>
          </Box>
        )}

        <Typography sx={{ textAlign: 'center', mt: 4 }}>
          <a href="/" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', textDecoration: 'none' }}>← Back to home</a>
        </Typography>
      </Box>
    </Box>
  );
}
